import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { NoItemContent } from "../page-bases/NoItemContent";
import { Box, Stack } from "@mui/material";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import { assetApi } from "../../services/assets/asset-crud";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import useAppNavigation from "../../hooks/useAppNavigation";
import { useAppDispatch } from "../../store/hooks";
import { updateAssetDetail } from "../../store/reducers/assets/assetDetail";
import {
  IAssetRecord,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters/AssetLogsFilter";
import AssetPageAnalytics from "../../common-ui/analytics/assets/AssetPageAnalytics";
import appRoutePath from "../../config/path";
import { ExportModal } from "../../components/Modals/ExportModal";
import { DamsAppAsset } from "../../assets/registry";
import { usePageModels } from "../../hooks/usePageActions";
import { appConfig } from "../../config/config";
import ActionModal from "../../components/Modals/ActionModal";

function AssetsMainPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const page_info = getEmptyPageInfo(pageState);
  const dispatch = useAppDispatch();
  const { models, closeModel, openModel } = usePageModels();
  const [pageAction, updatePageAction] = useState<PageActions>();

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const fetchData = async () => {
    const result = await assetApi.getAssets();
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand: `${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `,
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const navigate = useAppNavigation();

  function addNewItem() {
    navigate({ path: appRoutePath.ADD_ASSET_REGISTRY_PAGE });
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function handleDelete(item: TRowItem) {
    actionDialogStore.open({
      title: "Delete Asset",
      subtitle: `${item.brand} ,model ${item.model} ,Are you sure you want to delete asset ?`,
      onRightBtnPress: async () => deleteRequest(item.id),
    });
  }

  function handleUpdate(item: TRowItem) {
    dispatch(updateAssetDetail(item as IAssetRecord));
    actionDialogStore.open({
      title: "Update Asset",
      subtitle: `${item.brand} ,model ${item.model}, Are you sure you want to proceed with the update?`,
      onRightBtnPress: async () =>
        navigate({
          path: appRoutePath.EDIT_ASSET_REGISTRY_PAGE,
          params: { id: item.id },
        }),
      rightBtnTxt: "continue",
      type: "DELETE",
    });
  }

  async function deleteRequest(id: string) {
    const res = await assetApi.deleteAsset(id);
    if (res.success) {
      fetchData();
    }
    alertDialogStore.open({
      title: res.success ? "Delete Asset" : "Delete Asset Failed",
      subtitle: res.message,
      isSuccess: res.success,
    });
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await assetApi.exportAssets({
        file_format: "csv",
        ...querySet,
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split("/").pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace(
          "/api/v1",
          "",
        )}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: "GET",
          });

          if (!downloadResponse.ok) {
            throw new Error("Download failed");
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || "assets_export.csv";

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true,
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, "_blank");

          updatePageAction({
            title: "Export Initiated",
            message:
              "CSV export has been initiated. Check your downloads folder.",
            success: true,
          });
        }

        openModel("alert");
      } else {
        throw new Error("Invalid response from export API");
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false,
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await assetApi.exportAssets({
        file_format: "pdf",
        ...querySet,
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split("/").pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace(
          "/api/v1",
          "",
        )}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: "GET",
          });

          if (!downloadResponse.ok) {
            throw new Error("Download failed");
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || "assets_export.pdf";

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true,
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, "_blank");

          updatePageAction({
            title: "Export Initiated",
            message:
              "PDF export has been initiated. Check your downloads folder.",
            success: true,
          });
        }

        openModel("alert");
      } else {
        throw new Error("Invalid response from export API");
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false,
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={assetApi.deleteAsset}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      pageState={pageState}
      title={"Asset Management"}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText="Add Asset"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={2}>
            {/* TODO: FETCH ANALYTICS DATA AND PASS TO AssetPageAnalytics */}
            {/* <ExpandableContainer label="Analytics" initOpenState>
              <AssetPageAnalytics />
            </ExpandableContainer> */}
            <AssetLogsFilter<IAssetRecord[]>
              ftn={assetApi.getAssets}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Assets"
              tableSubtitle="Manage assets and details"
              numTableRecords="25"
              tableTitleBadge={
                selectedRowItems.length === 0
                  ? undefined
                  : selectedRowItems.length + " selected"
              }
              data={dataset}
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              hasPagination
              paginationState={pagination}
              maxRowCells={8}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              onTableItemDelete={handleDelete}
              onTableItemEdit={handleUpdate}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Assets Data"
              subtitle="Choose your preferred format to export assets data"
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={closeModel}
              rightBtnTxt={"continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Devices Found",
    message:
      'No Devices have been added yet. Click the "Add New" button to add a new Device.',
  },

  "empty-search": {
    title: "No Devices Found",
    message:
      "No Devices found in your database. Please add a new Device to find them in the Devices tab ",
  },
};

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Date Received",
];
const hkeys: (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw",
];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)",
  "Additional Information",
];

export { AssetsMainPage };
